// Scenario-based challenges - Detective, Rescue, Boss Battles, Speed Runs

const scenarios = {
  linux: [
    {
      id: 'linux-detective-1',
      type: 'detective',
      title: 'The Rogue Process',
      narrative: 'Alert! A mysterious process is consuming 100% CPU on your production server. Users are complaining about slow response times. Find and terminate the culprit before the server crashes!',
      difficulty: 'intermediate',
      xpReward: 150,
      timeLimit: null,
      steps: [
        {
          task: 'First, list all processes sorted by CPU usage to find the culprit.',
          hint: 'Use ps with sorting options, or try the top command',
          answer: ['ps aux --sort=-%cpu', 'ps aux --sort=-pcpu', 'top -bn1', 'htop'],
          explanation: 'ps aux shows all processes. --sort=-%cpu sorts by CPU usage in descending order.'
        },
        {
          task: 'You found a process called "cryptominer" with PID 31337. Terminate it immediately!',
          hint: 'Use kill with the appropriate signal to force stop',
          answer: ['kill -9 31337', 'kill -KILL 31337', 'kill -SIGKILL 31337'],
          explanation: 'kill -9 sends SIGKILL which cannot be ignored, forcing immediate termination.'
        },
        {
          task: 'Verify the process is gone by checking if PID 31337 still exists.',
          hint: 'Use ps to check for a specific PID',
          answer: ['ps -p 31337', 'ps aux | grep 31337', 'kill -0 31337'],
          explanation: 'ps -p checks for a specific PID. If the process is gone, it will show no results.'
        }
      ]
    },
    {
      id: 'linux-rescue-1',
      type: 'rescue',
      title: 'Disk Space Emergency',
      narrative: 'CRITICAL: Your server disk is 95% full! The application will crash if we run out of space. You need to find and remove large files to save the day!',
      difficulty: 'advanced',
      xpReward: 200,
      timeLimit: null,
      steps: [
        {
          task: 'Find the 10 largest files in the /var directory.',
          hint: 'Use du to analyze disk usage and sort the results',
          answer: [
            'du -ah /var | sort -rh | head -10',
            'du -ah /var 2>/dev/null | sort -rh | head -10',
            'find /var -type f -exec du -h {} + | sort -rh | head -10'
          ],
          explanation: 'du -ah shows all files with human-readable sizes. sort -rh sorts by human-readable sizes in reverse.'
        },
        {
          task: 'You found old log files. Delete all .log files in /var/log older than 30 days.',
          hint: 'Use find with -mtime and -delete',
          answer: [
            'find /var/log -name "*.log" -mtime +30 -delete',
            'find /var/log -type f -name "*.log" -mtime +30 -delete'
          ],
          explanation: '-mtime +30 finds files modified more than 30 days ago. -delete removes them.'
        },
        {
          task: 'Check how much disk space is now free on the root partition.',
          hint: 'Use df to show disk free space',
          answer: ['df -h', 'df -h /', 'df -h /var'],
          explanation: 'df -h shows disk usage in human-readable format.'
        }
      ]
    },
    {
      id: 'linux-boss-1',
      type: 'boss',
      title: 'The Log Analysis Gauntlet',
      narrative: 'You are the on-call engineer and need to analyze logs to diagnose a production issue. Multiple users are reporting errors. Find the pattern!',
      difficulty: 'expert',
      xpReward: 300,
      timeLimit: null,
      steps: [
        {
          task: 'Count how many ERROR lines are in /var/log/app.log',
          hint: 'Use grep with counting option',
          answer: ['grep -c ERROR /var/log/app.log', 'grep -c "ERROR" /var/log/app.log'],
          explanation: 'grep -c counts matching lines instead of displaying them.'
        },
        {
          task: 'Extract all unique IP addresses from the error lines in the log.',
          hint: 'Combine grep, awk or cut, and sort -u',
          answer: [
            "grep ERROR /var/log/app.log | grep -oE '[0-9]+\\.[0-9]+\\.[0-9]+\\.[0-9]+' | sort -u",
            "grep ERROR /var/log/app.log | awk '{print $1}' | sort -u"
          ],
          explanation: 'Chaining commands with pipes lets you filter and process data step by step.'
        },
        {
          task: 'Find all errors from the last hour and save them to /tmp/recent_errors.log',
          hint: 'Use grep or awk with timestamp filtering, then redirect output',
          answer: [
            'grep ERROR /var/log/app.log | tail -100 > /tmp/recent_errors.log',
            "awk '/ERROR/' /var/log/app.log | tail -100 > /tmp/recent_errors.log"
          ],
          explanation: 'The > operator redirects output to a file.'
        },
        {
          task: 'Count errors per IP address and show the top 5 offenders.',
          hint: 'Use a combination of grep, awk, sort, and uniq -c',
          answer: [
            "grep ERROR /var/log/app.log | awk '{print $1}' | sort | uniq -c | sort -rn | head -5"
          ],
          explanation: 'uniq -c counts occurrences. sort -rn sorts numerically in reverse order.'
        }
      ]
    }
  ],

  docker: [
    {
      id: 'docker-detective-1',
      type: 'detective',
      title: 'Container Mystery',
      narrative: 'Your web application is showing 502 Bad Gateway errors. The container seems to be running but something is wrong. Investigate and fix the issue!',
      difficulty: 'intermediate',
      xpReward: 150,
      timeLimit: null,
      steps: [
        {
          task: 'Check the status of all containers to find the problematic one.',
          hint: 'Use docker ps with the -a flag to see all containers',
          answer: ['docker ps -a', 'docker ps --all', 'docker container ls -a'],
          explanation: 'docker ps -a shows all containers including stopped ones.'
        },
        {
          task: 'A container named "webapp" keeps restarting. Check its logs for errors.',
          hint: 'Use docker logs to see container output',
          answer: ['docker logs webapp', 'docker logs --tail 100 webapp'],
          explanation: 'docker logs shows the stdout/stderr output from the container.'
        },
        {
          task: 'The logs show a missing environment variable. Restart the container with ENV DEBUG=true',
          hint: 'Use docker run with -e flag for environment variables',
          answer: [
            'docker run -e DEBUG=true webapp',
            'docker run -d -e DEBUG=true webapp',
            'docker run --env DEBUG=true webapp'
          ],
          explanation: '-e or --env sets environment variables inside the container.'
        }
      ]
    },
    {
      id: 'docker-boss-1',
      type: 'boss',
      title: 'Full Stack Deployment',
      narrative: 'Deploy a complete web application stack: build the app, create a network, run the database, and connect everything together!',
      difficulty: 'expert',
      xpReward: 400,
      timeLimit: null,
      steps: [
        {
          task: 'Create a custom Docker network called "app-network" for container communication.',
          hint: 'Use docker network create',
          answer: ['docker network create app-network'],
          explanation: 'Custom networks allow containers to communicate by name.'
        },
        {
          task: 'Run a PostgreSQL database on the network with password "secretpass".',
          hint: 'Use docker run with -e for environment, --network for the network',
          answer: [
            'docker run -d --name db --network app-network -e POSTGRES_PASSWORD=secretpass postgres',
            'docker run -d --network app-network --name db -e POSTGRES_PASSWORD=secretpass postgres'
          ],
          explanation: 'The POSTGRES_PASSWORD env var is required for the postgres image.'
        },
        {
          task: 'Build your web application image from the current directory and tag it as "myapp:v1".',
          hint: 'Use docker build with -t flag',
          answer: ['docker build -t myapp:v1 .'],
          explanation: '-t tags the image. The . specifies the build context.'
        },
        {
          task: 'Run the web app container on the same network, exposing port 3000 to host port 8080.',
          hint: 'Use docker run with -p for ports and --network',
          answer: [
            'docker run -d --name webapp --network app-network -p 8080:3000 myapp:v1',
            'docker run -d -p 8080:3000 --network app-network --name webapp myapp:v1'
          ],
          explanation: '-p HOST:CONTAINER maps ports. --network connects to our custom network.'
        },
        {
          task: 'Verify both containers are running and connected to app-network.',
          hint: 'Use docker network inspect',
          answer: ['docker network inspect app-network'],
          explanation: 'network inspect shows all containers connected to that network.'
        }
      ]
    }
  ],

  networking: [
    {
      id: 'net-detective-1',
      type: 'detective',
      title: 'The Unreachable Server',
      narrative: 'Users report they cannot access the API at api.example.com:8080. You need to diagnose whether the problem is DNS, connectivity, or the service itself.',
      difficulty: 'intermediate',
      xpReward: 150,
      timeLimit: null,
      steps: [
        {
          task: 'First, check if DNS resolution is working for api.example.com.',
          hint: 'Use host, nslookup, or dig to query DNS',
          answer: ['host api.example.com', 'nslookup api.example.com', 'dig api.example.com'],
          explanation: 'DNS lookup tools resolve domain names to IP addresses.'
        },
        {
          task: 'DNS works! Now check if the server is reachable with ping.',
          hint: 'Use ping to test basic connectivity',
          answer: ['ping api.example.com', 'ping -c 4 api.example.com'],
          explanation: 'Ping tests ICMP connectivity to the host.'
        },
        {
          task: 'Server responds to ping. Check if port 8080 is open using netcat.',
          hint: 'Use nc with -zv flags for port scanning',
          answer: [
            'nc -zv api.example.com 8080',
            'netcat -zv api.example.com 8080'
          ],
          explanation: '-z is zero-I/O mode (scanning), -v is verbose.'
        },
        {
          task: 'Port is open! Make an HTTP request to check if the API responds.',
          hint: 'Use curl to make an HTTP request',
          answer: [
            'curl http://api.example.com:8080',
            'curl -v http://api.example.com:8080'
          ],
          explanation: 'curl makes HTTP requests and shows the response.'
        }
      ]
    },
    {
      id: 'net-boss-1',
      type: 'boss',
      title: 'API Integration Challenge',
      narrative: 'You need to integrate with a REST API. Authenticate, fetch data, and update a resource using curl.',
      difficulty: 'expert',
      xpReward: 350,
      timeLimit: null,
      steps: [
        {
          task: 'Get the API headers to understand the endpoints available.',
          hint: 'Use curl with -I for headers only',
          answer: ['curl -I https://api.example.com', 'curl --head https://api.example.com'],
          explanation: '-I sends a HEAD request, showing only response headers.'
        },
        {
          task: 'Authenticate using Basic Auth with username "admin" and password "secret".',
          hint: 'Use curl with -u for authentication',
          answer: ['curl -u admin:secret https://api.example.com/login'],
          explanation: '-u sends Basic Authentication credentials.'
        },
        {
          task: 'Send a POST request with JSON body {"name":"test","value":42} to /api/data.',
          hint: 'Use curl with -X POST, -H for Content-Type, and -d for data',
          answer: [
            'curl -X POST -H "Content-Type: application/json" -d \'{"name":"test","value":42}\' https://api.example.com/api/data'
          ],
          explanation: 'POST with JSON requires setting the Content-Type header.'
        },
        {
          task: 'Update resource 123 using PUT request with {"status":"active"}.',
          hint: 'Use curl with -X PUT',
          answer: [
            'curl -X PUT -H "Content-Type: application/json" -d \'{"status":"active"}\' https://api.example.com/api/data/123'
          ],
          explanation: 'PUT is typically used for updating existing resources.'
        },
        {
          task: 'Delete resource 123 from the API.',
          hint: 'Use curl with -X DELETE',
          answer: ['curl -X DELETE https://api.example.com/api/data/123'],
          explanation: 'DELETE requests remove resources from the server.'
        }
      ]
    }
  ],

  git: [
    {
      id: 'git-rescue-1',
      type: 'rescue',
      title: 'Undo the Disaster',
      narrative: 'A developer accidentally committed sensitive data and pushed to main! You need to remove the commit and clean up the history before anyone notices.',
      difficulty: 'advanced',
      xpReward: 200,
      timeLimit: null,
      steps: [
        {
          task: 'First, check the git log to see recent commits.',
          hint: 'Use git log with --oneline for compact view',
          answer: ['git log --oneline', 'git log -5', 'git log'],
          explanation: 'git log shows the commit history. --oneline makes it compact.'
        },
        {
          task: 'The bad commit is the latest one. Undo it while keeping changes staged.',
          hint: 'Use git reset with --soft to keep changes',
          answer: ['git reset --soft HEAD~1', 'git reset --soft HEAD^'],
          explanation: '--soft keeps changes staged, ready to be recommitted.'
        },
        {
          task: 'Remove the sensitive file "secrets.env" from staging.',
          hint: 'Use git reset HEAD to unstage, or git rm --cached',
          answer: ['git rm --cached secrets.env', 'git reset HEAD secrets.env'],
          explanation: 'git rm --cached removes from staging without deleting the file.'
        },
        {
          task: 'Add secrets.env to .gitignore to prevent future accidents.',
          hint: 'Use echo to append to .gitignore',
          answer: [
            'echo "secrets.env" >> .gitignore',
            'echo secrets.env >> .gitignore'
          ],
          explanation: '>> appends to a file without overwriting existing content.'
        }
      ]
    }
  ]
};

// Get scenarios by topic
export const getScenariosByTopic = (topic) => {
  return scenarios[topic] || [];
};

// Get scenario by ID
export const getScenarioById = (id) => {
  for (const topicScenarios of Object.values(scenarios)) {
    const found = topicScenarios.find(s => s.id === id);
    if (found) return found;
  }
  return null;
};

// Get all scenarios
export const getAllScenarios = () => {
  return Object.entries(scenarios).flatMap(([topic, topicScenarios]) =>
    topicScenarios.map(s => ({ ...s, topic }))
  );
};

export default scenarios;
